<template>
  <Address
    :address
    :edit-button-title="i18n(translations.edit)"
    :show-form="isFormVisible"
    :title="i18n(translations.title)"
    :with-details="!collectionPoint"
    @edit="onEdit"
  >
    <ShippingAddressForm
      :address="shippingAddress"
      :api-errors
      :countries="formatCountries(shippableCountries)"
      :submit-button-label="i18n(translations.submitButton)"
      :submitting
      :user-information="{}"
      :with-title="false"
      @submit="onSubmit"
    />
  </Address>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { Country } from '@backmarket/http-api'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useLogUnhandledAPIErrors } from '@backmarket/nuxt-module-address/useLogUnhandledAPIErrors'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import {
  getPhoneNumberInfos,
  getPhoneNumberToE164,
} from '@ds/components/InputPhone'

import type { Deliver } from '~/api/cart/types'
import ShippingAddressForm from '~/scopes/checkout/components/ShippingAddress/components/ShippingAddressForm/ShippingAddressForm.vue'
import useHandleUnauthorizedUser from '~/scopes/checkout/composables/useHandleUnauthorizedUser'
import { getAPIErrors } from '~/scopes/checkout/utils/getAPIErrors'
import { isCountryShippable } from '~/scopes/checkout/utils/isCountryShippable'

import { formatCountries } from '../../utils/formatCountries'
import Address from '../Address/Address.vue'

import translations from './ShippingAddress.translations'

const props = defineProps<{
  address: ApiSpecs['/bm/cart']['GET']['response']['addressList']['deliver']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  collectionPoint?: ApiSpecs['/bm/cart']['GET']['response']['addressList']['deliverCollectionPoint']
}>()

const emit = defineEmits<{
  (e: 'updating'): void
  (e: 'updated', callback?: () => void): void
}>()

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const {
  market: { countryCode },
} = useMarketplace()
const { user } = useUserStore()
const tracking = useTracking()

const { openErrorToast } = useTheToast()
const { logUnhandledAPIErrors } = useLogUnhandledAPIErrors()
const { handleUnauthorizedUser } = useHandleUnauthorizedUser()

const isFormVisible = ref(
  !isCountryShippable(props.address?.country, props.shippableCountries),
)
const submitting = ref(false)
const apiErrors = ref({})

const defaultAddress: Deliver = {
  birthdate: null,
  city: null,
  company: null,
  country: null,
  countryDialInCode: null,
  customerIdNumber: null,
  dateCreation: null,
  dateModification: null,
  email: null,
  firstName: null,
  firstNamePronunciation: null,
  lastName: null,
  lastNamePronunciation: null,
  phone: null,
  postalCode: null,
  stateOrProvince: null,
  street: null,
  street2: null,
  warnings: [],
}

const shippingAddress = computed(() => {
  const address = props.address ?? defaultAddress
  const phone = `${address.phone || user.value.phone || ''}`
  const countryDialInCode = `${address.countryDialInCode || ''}`

  return {
    ...address,
    // Some fields can be prefilled from userStore or the current country
    country: address.country || (countryCode as unknown as Country),
    firstName: address.firstName || user.value.firstName || '',
    lastName: address.lastName || user.value.lastName || '',
    // We need to format the phone number
    phone:
      phone && countryDialInCode
        ? getPhoneNumberToE164(countryDialInCode, phone)
        : phone,
  }
})

watch(
  () => isFormVisible,
  (isVisible) => {
    if (isVisible) {
      tracking.trackBlockImpression({
        name: 'shipping_address_form',
      })
    }
  },
  { immediate: true },
)

async function onEdit() {
  tracking.trackClick({
    name: 'edit_shipping_address',
    zone: 'address',
  })

  isFormVisible.value = true
}

async function onSubmit(values: Deliver) {
  emit('updating')
  apiErrors.value = {}
  submitting.value = true

  try {
    const { dial, nationalNumber } = getPhoneNumberInfos(
      values.phone ?? '',
      values.country as Country,
    )

    await $httpFetch('/bm/client/addresses/v2/shipping', {
      method: 'POST',
      body: removeEmptyValuesInObject<Deliver>({
        ...values,
        phone: nationalNumber,
        countryDialInCode: dial,
      }) as Deliver,
    })

    tracking.trackClick({
      name: 'shipping_address_saved',
      zone: 'address',
    })

    emit('updated', () => {
      isFormVisible.value = false
      submitting.value = false
    })
  } catch (e) {
    const error = e as Record<string, unknown>

    tracking.trackClick({
      name: 'shipping_address_saved_failure',
      zone: 'address',
    })

    if (error?.status === 400 || error?.status === 422) {
      const errors = getAPIErrors(error)
      logUnhandledAPIErrors(Object.keys(errors))
      apiErrors.value = errors
      openErrorToast()
    } else
      await handleUnauthorizedUser(
        error,
        '[CHECKOUT] Failed to submit shipping form',
      )

    submitting.value = false
  }
}
</script>
